/*
* Copyright © 2019 Insticator, Inc. All Rights Reserved.
*
* CONFIDENTIALITY NOTICE: THIS SOFTWARE CONTAINS CONFIDENTIAL INFORMATION OF
* INSTICATOR, INC. USE, DISCLOSURE OR REPRODUCTION IS PROHIBITED WITHOUT THE PRIOR
* EXPRESS WRITTEN PERMISSION OF INSTICATOR, INC.
*/

/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import Component from 'inferno-component';
import ProgressBar from 'components/shared/progressbar/progressbar';
import Question from 'components/content/shared/questionset/shared/question/question_container';
import InputField from 'components/shared/fields/input/input_container';
import RedeemScreen from 'components/screens/redeem/redeem_container';
import UserMessage from 'components/shared/usermessage/usermessage';
import RewardsFeedback from 'components/shared/rewardsfeedback/rewardsfeedback_container';
import AnswerErrorScreen from 'components/screens/answererror/answererror_container';

// STYLES
// (css-in-js, imported as JS from external file, turned into CSS via library used in utils)
import styles from 'components/content/shared/questionset/input/input_styles';
import { createStyles } from 'utils/styles';
import { preloadStyleImages } from "utils/preloader";
import { trackEvent } from 'utils/tracking/tracking';
import { refreshAds } from 'utils/ads';
import { url } from 'utils/urls';
import { makePOSTRequest } from "utils/api/makePOSTRequest";
import { log } from 'utils/logger';
import Timer from 'utils/timer';
preloadStyleImages(styles);
const { classes } = createStyles(styles);


/* ========== ~~~~~~~~~~ INPUT SET ~~~~~~~~~~ ========== */
class InputSet extends Component {
    constructor(props) {
        super(props);
        this.resultTimeout = null;
        this.timer = new Timer();
        this.timer.start();
        this.resultWaitTime = new Timer();
        this.amountOfTimeBeforeAnswering = 0;
        this.trackingAnswer = null;
        this.resultProcessed = false;
    }

    componentWillReceiveProps(nextProps) {
        const thisPropsFrameState = this.props.data.frameStatus[this.props.data.frame]['state'];
        const nextPropsFrameState = nextProps.data.frameStatus[this.props.data.frame]['state'];

        // PROCESS RESULT / RECEIVED QUESTION RESPONSE RESULT
        if(thisPropsFrameState === 'clicked' && nextPropsFrameState === 'result') {
            this.processClickedToResult(nextProps);

            // PROCESS NETWORK ERROR
        } else if (thisPropsFrameState === 'clicked' && nextPropsFrameState === 'networkError') {
            this.processClickedToError();
        }
    }

    processClickedToResult = nextProps => {
        // track question answered
        this.trackQuestionAnswered(nextProps)
        // handle points award
        this.handlePointsAward(nextProps);
        // handle result phase
        this.handleResultPhase();
        // remove loader
        this.props.showLoader(false);
    };

    processClickedToError = () => {
        // track Response Error
        // this.trackResponseError(this.trackingAnswer);
        // remove loader
        this.props.showLoader(false);
        // show error screen
        this.props.displayScreen({
            name: 'answerError',
            type: 'full',
            visible: true,
            content: <AnswerErrorScreen contentId={this.props.contentId} skipQuestion={this.handleSkipQuestion} />
        })
    };

    getFrameStatus = () =>
        this.props.frameData.type !== 'quiz' ? 'answered' : this.props.isCorrectAnswer ? 'correct' : 'incorrect';

    handleResultPhase = () => {
        // Setting an appropriate timeframe for the Result Phase
        this.resultTimeout = setTimeout(() => {
            // After the phase is over, move to check if the next (rewards) phase is enabled.
            clearTimeout(this.resultTimeout);
            this.props.advanceFrame(this.props.contentId);
        }, this.props.currentContentSettings.uniqueProps.showResultForXSeconds);
    };

    getResultClass = () => (this.props.data.showResultForXSeconds === null || this.props.data.frameStatus !== undefined) && this.props.data.frameStatus[this.props.data.frame]['state'];

    processSubmit = answer => {
        this.trackingAnswer = answer;
        // start result wait time timer
        this.resultWaitTime.start();
        // set frame status to 'clicked'
        this.props.setAnswerClicked(this.props.contentId, this.props.data.frame, answer.id);
        // show loader
        this.props.showLoader(true);
        // track answer
        this.trackAnswer(answer);
        // refresh advertisements
        refreshAds();
    };

    handleSkipQuestion = () => this.props.advanceFrame(this.props.contentId);

    trackAnswer = answer => this.props.requestQuestionResponseResult(this.props.contentId, this.props.data.frame, false, this._getRequestData(answer), this.props.isPubPortalPreview);

    getQuestionnaireType = () => this.props.frameData.type === 'profile' ? this.props.currentContentSettings.uniqueProps.questionnaireType.toLowerCase() : null;

    _getRequestData = answer => ({
        cookieId: this.props.userInfo.userId,
        embedUUID: this.props.embedUUID,
        contentUUID: this.props.data.frameData.question.id,
        contentSignature: this.props.frameData.questionSignature,
        contentType: this.getQuestionnaireType(),
        pageUrl: window.parent.document.location.href,
        contentProperties: {
            questionText: this.props.data.frameData.question.text,
            optionText: answer,
            optionUUID: null
        }
    });


    ///// =========== ~~~ =========== START: REWARDS LOGIC =========== ~~~ ===========/////
    getRewardPoints = (nextProps) => {
        if (!nextProps.data.frameStatus[this.props.data.frame]['validRequest']) return 0; // Do't award any points if a question result request is not valid;
        const questionnaireType = this.getQuestionnaireType();
        // NEW SETTINGS FALLBACK
        if (!!this.props.rewardsSettings.pointValues.profileQuestions && questionnaireType !== null) {
            return this.props.rewardsSettings.pointValues.profileQuestions[questionnaireType];
        } else if (!!this.props.rewardsSettings.pointValues['profile']) {
            return this.props.rewardsSettings.pointValues['profile'];
        } else {
            return 0;
        }
    };

    handlePointsAward = (nextProps) => {
        const pointsEarnedAfterReward = this.props.rewardsStatus.pointsEarned - this.props.rewardsStatus.numberOfRewardsEarned * this.props.rewardsSettings.redeemAfter;
        const pointsEarnedForQuestion = this.getRewardPoints(nextProps);
        const currentPointsBalance = pointsEarnedAfterReward + pointsEarnedForQuestion;
        if (currentPointsBalance >= this.props.rewardsSettings.redeemAfter) {
            const rewardsEarned = Math.floor(currentPointsBalance / this.props.rewardsSettings.redeemAfter);
            this.props.awardPointsWithRewards(pointsEarnedForQuestion, rewardsEarned, true);
            // fire show screen action
            this.showRedeemScreen(nextProps);
        } else {
            // Delay points award to synchronize award with +1 badge animation
            this.props.awardPoints(pointsEarnedForQuestion);
        }
    };

    showRedeemScreen = () => {
        this.props.displayScreen({
            name: 'redeem',
            type: 'full',
            visible: true,
            content: <RedeemScreen contentId={this.props.contentId} />
        })
    };

    ///// =========== ~~~ =========== END: REWARDS LOGIC =========== ~~~ ===========/////


    ////// --------------------------- START: TRACKING --------------------------- //////

    trackQuestionAnswered = (nextProps) => {
        const additionalProps = {
            amountOfTimeBeforeAnswering: this.timer.end(),
            resultWaitTime: this.resultWaitTime.end(),
            question: this.props.frameData.question,
            answer: this.trackingAnswer,
            questionnaire: this.props.data.currentQuestionnaire ? this.props.data.currentQuestionnaire : null,
            validRequest: nextProps.data.frameStatus[this.props.data.frame]['validRequest'],
            isCorrect: false
        }; // passed as milliseconds based on dates (from utils/timer)
        // This functionality is deprecated
        // trackEvent({ type: 'click', name: 'questionAnswered', props: this.props, additionalProps }); // passed as milliseconds based on dates (from utils/timer)
    };

    // trackResponseError = (answer) => {
    //     const additionalProps = {
    //         amountOfTimeBeforeAnswering: this.amountOfTimeBeforeAnswering,
    //         resultWaitTime: this.resultWaitTime.end(),
    //         question: this.props.frameData.question,
    //         answer: answer,
    //         questionnaire: this.props.currentQuestionnaire ? this.props.currentQuestionnaire : null
    //     };
    //     trackEvent('click', 'questionResultError', this.props, { ...additionalProps });
    // };

    ////// --------------------------- END: TRACKING --------------------------- //////

    render() {
        return(
            <div className={`${classes.typeset} type-${this.props.data.frameData.question.type} parentContentType-${this.props.contentType} ${this.getResultClass()}`}>
                {this.props.data.showFrameForXSeconds != null ? <ProgressBar type={`frame`} duration={this.props.data.showFrameForXSeconds} fillingDelay={0} direction={`leftToRight`} isActive={this.props.data.showFrameForXSeconds != null} shouldKillItself={this.getResultClass() === 'result'}/> : null}
                <Question contentId={this.props.data.contentId} frameData={this.props.data.frameData} />
                <div className={`${classes.answerset} answerset answeramount-${this.props.data.frameData.answers.length}`}>
                    <InputField contentId={this.props.data.contentId} id={`testid`} placeholder={this.props.data.frameData.placeholder} validation={this.props.data.frameData.question.validation} processSubmit={this.processSubmit} hasButton />
                </div>
                <div className={`${classes.resultelements} resultelements`}>
                    <UserMessage hasRewardsComponent={this.props.hasRewardsComponent}>
                        <RewardsFeedback contentId={this.props.data.contentId} frame={this.props.data.frame}/>
                    </UserMessage>
                    <ProgressBar type={`result`} duration={this.props.data.showResultForXSeconds} direction={`leftToRight`} fillingDelay={2400} isActive={this.getResultClass() === 'result'} shouldKillItself={false} />
                </div>
            </div>
        );
    }
}



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default InputSet;
